import PropTypes from 'prop-types'
import WordpressSEO from '../WordpressSEO'
import { useContext, useEffect, useState } from 'react'
import { GlobalStateContext } from '../../context/GlobalContextProvider'
import { useRouter } from 'next/router'
import { LIVE_SITE_URL } from '@constants'
import useClearTimeout from '../../hooks/useClearTimeout'
import ModelMeasurements from '../ModelMeasurements'
import classNames from 'classnames'
import s from '../../pages/[site]/model/model.module.css'
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock'
import Layout from '../Layout'
import BackButton from '../BackButton'
import ModelHeader from '../ModelHeader'
import Title from '../Title'
import InstagramCount from '../InstagramCount'
import TiktokCount from '../TiktokCount'
import ModelGallery from '../ModelGallery'

function ModelPage({
  modelSlug,
  site,
  title,
  url,
  seo,
  gender,
  firstName,
  lastName,
  instagramCount,
  instagram,
  tiktokCount,
  tiktok,
  measurements,
  image,
  uiColour,
  uiLogoColour,
  globalSettings,
  galleries,
  ...props
}) {
  const state = useContext(GlobalStateContext)
  const router = useRouter()
  const [headerColour, setHeaderColour] = useState('')
  const [mobileMenu, setMobileMenu] = useState(false)
  const [shareText, setShareText] = useState('Share Model')
  const [timeoutId, setTimeoutId] = useState(null)
  const [emailsVisible, setEmailsVisible] = useState(false)
  const headerScrolled = state?.header?.scrolled?.scrolled
  const pageLink = `${LIVE_SITE_URL}${router.asPath}`

  // const enableTikTok =
  //   parseInt(process.env.NEXT_PUBLIC_ENABLE_TIKTOK ?? '0', 10) === 1
  // const enableTikTokCount =
  //   parseInt(process.env.NEXT_PUBLIC_ENABLE_TIKTOK_COUNT ?? '0', 10) === 1

  const isLightUi = uiColour === 'light'
  const isDarkUi = uiColour === 'dark'
  const isLightLogo = uiLogoColour === 'light'
  const isDarkLogo = uiLogoColour === 'dark'

  useClearTimeout(timeoutId)

  useEffect(() => {
    const doc = document.documentElement
    const headerAttr = 'data-header-color'
    if (isLightUi && isDarkLogo && !headerScrolled) {
      setHeaderColour('light-ui-dark-logo')
      doc.setAttribute(headerAttr, headerColour)
    } else if (isDarkUi && isLightLogo && !headerScrolled) {
      setHeaderColour('dark-ui-light-logo')
      doc.setAttribute(headerAttr, headerColour)
    } else if (isLightUi && isLightLogo && !headerScrolled) {
      setHeaderColour('light')
      doc.setAttribute(headerAttr, headerColour)
    } else {
      setHeaderColour('')
      doc.setAttribute(headerAttr, headerColour)
    }
  }, [state, headerScrolled, uiColour, uiLogoColour])

  const setInstagram = instagram
    ? [{ username: instagram }, { count: instagramCount }]
    : null

  const setTiktok = tiktok
    ? [{ username: tiktok }, { count: tiktokCount }]
    : null

  const setMeasurements = (classname = '', isMobile = false) => {
    if (!measurements) return null
    return (
      <ModelMeasurements
        className={classNames(s.measurementsWrapper, classname)}
        listClassName={s.measurements}
        itemClassName={s.measurement}
        valueClassName={s.measurementValue}
        measurements={measurements}
        shoesToggle
        mobile={isMobile}
        male={
          (typeof gender === 'string' || gender instanceof String) &&
          gender.toLowerCase() === 'male'
        }
      />
    )
  }

  const handleOpenMenu = () => {
    setMobileMenu(true)
    disableBodyScroll()
  }

  const handleCloseMenu = () => {
    setMobileMenu(false)
    clearAllBodyScrollLocks()
  }

  const handleEmailToggle = () => {
    setEmailsVisible(!emailsVisible)
  }

  const handleShare = () => {
    let copyLink = document.getElementById('model-share-link')
    copyLink.select()
    document.execCommand('copy')

    setShareText('Link copied')
    const timeoutId = setTimeout(() => {
      setShareText('Share Model')
    }, 1000)
    setTimeoutId(timeoutId)
  }

  return (
    <Layout globalSettings={globalSettings}>
      <WordpressSEO
        url={url || `/${site}/model/${modelSlug}`}
        title={title}
        seo={seo}
        image={image?.sourceUrl}
        canonical={`/${site}/model/${modelSlug}`}
      />
      {/* TODO: We have used this layout in multiple places. Should try turn into component */}
      <div
        className={classNames(s.mobileHeader, {
          [s.mobileMenuVisible]: mobileMenu === true,
        })}
      >
        <BackButton
          className={s.mobileBackBtn}
          fallback={
            router.query?.boardType
              ? `${site}/${router.query?.boardType}`
              : site
          }
        />
        <h1
          className={s.mobileTitle}
          dangerouslySetInnerHTML={{ __html: `${firstName} ${lastName || ''}` }}
        />
        <button
          type="button"
          className={s.mobileMenuBtn}
          onClick={handleOpenMenu}
        >
          <span />
        </button>
        <div className={s.mobileMenu}>
          <div className={s.mobileMenuItem}>
            <button type="button" className={s.shareBtn} onClick={handleShare}>
              <span className={s.mobileMenuLabel}>{shareText}</span>
              <input
                id="model-share-link"
                className={s.shareLink}
                type="text"
                defaultValue={pageLink}
              />
            </button>
          </div>
          {/* <div className={s.mobileMenuItem}>
            <a
              href={`https://twitter.com/share?text=Check out ${firstName} ${lastName} on Select Model London&url=${LIVE_SITE_URL}${router.asPath}&hashtags=selectmodellondon`}
              className={s.mobileMenuLabel}
              target="_blank"
              rel="noopener noreferrer"
            >
              Share on Twitter
            </a>
          </div> */}
        </div>
        <button
          type="button"
          className={s.closeMenuBtn}
          onClick={handleCloseMenu}
        />
      </div>
      <ModelHeader
        coverImage={image?.sourceUrl}
        firstName={firstName}
        lastName={lastName}
        bio={props.bio}
        instagram={setInstagram}
        tiktok={setTiktok}
        uiColour={isLightUi}
        logoColour={isLightLogo}
      />
      <div className={s.mobileInfo}>
        <Title className={s.title} title={firstName} subtitle={lastName} />
        <InstagramCount className={s.social} instagram={setInstagram} />
        <TiktokCount className={s.social} tiktok={setTiktok} />
        {/* // TODO: Turn this into a component and use in model gallery */}
        {props.bookers?.length > 0 && (
          <>
            <button
              type="button"
              className={classNames(s.toggleEmails, {
                [s.toggleEmailsVisible]: emailsVisible,
              })}
              onClick={handleEmailToggle}
            >
              Enquire
            </button>
            {emailsVisible && (
              <div className={s.emails}>
                {props.bookers?.map(({ acfTeamMembers }) => (
                  <a
                    key={acfTeamMembers?.email}
                    href={`mailto:${props.bookers
                      ?.map((e) => e.acfTeamMembers?.email)
                      .join(',')}?subject=${firstName}${
                      lastName ? `%20${lastName}` : ''
                    }%20-%20Enquiry&body=https://www.selectmodel.com/${site}/model/${modelSlug}`}
                    className={s.email}
                  >
                    {acfTeamMembers?.email}
                  </a>
                ))}
              </div>
            )}
          </>
        )}
        {setMeasurements(s.measurementsWrapperMobile, true)}
      </div>
      {/* NOTE: fallbackImg is a hold over from some refactoring work. If set to true (as it is here) it skips the first image in the portfolio (as that's used in the header) */}
      <ModelGallery
        fallbackImg={true}
        instagram={setInstagram}
        tiktok={setTiktok}
        firstName={firstName}
        lastName={lastName}
        galleries={galleries}
        props={props}
      />
      {setMeasurements(s.measurementsWrapperDesktop)}
    </Layout>
  )
}

ModelPage.propTypes = {
  pageUrl: PropTypes.string,
  pageTitle: PropTypes.string,
  pageDesc: PropTypes.string,
  seo: PropTypes.object,
  gender: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOfType[null]]),
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  instagramCount: PropTypes.number,
  instagram: PropTypes.string,
  tiktokCount: PropTypes.number,
  tiktok: PropTypes.string,
  measurements: PropTypes.array,
  image: PropTypes.object,
  uiColour: PropTypes.string,
  uiLogoColour: PropTypes.string,
  globalSettings: PropTypes.object,
  props: PropTypes.object,
}
ModelPage.defaultProps = {
  pageUrl: '',
  pageTitle: '',
  pageDesc: '',
  seo: {},
  gender: null,
  firstName: '',
  lastName: '',
  instagramCount: 0,
  instagram: '',
  tiktokCount: 0,
  tiktok: '',
  measurements: [],
  image: {},
  uiColour: 'dark',
  uiLogoColour: 'dark',
  globalSettings: {},
  props: {},
}

export default ModelPage
